import { useEffect, useMemo, useState } from 'react'
import { apiPlayerList, apiTransferListUser } from '../server/ServiceCall'
import { AuthState } from '../store/auth'

type SortKey =
  | 'POSITION' | 'PLAYER_NAME' | 'NATIONALITY' | 'AGE'
  | 'OVERALL'  | 'STAMINA'     | 'POTENTIAL'   | 'SALARY' | 'CONTRACT_END'
type SortDir = 'asc' | 'desc'

const POSITIONS = ['', 'GK', 'CB', 'LB', 'RB', 'DM', 'CM', 'AM', 'LW', 'RW', 'ST']

interface PlayerRow {
  PLAYER_CODE:    number
  PLAYER_NAME:    string
  NATIONALITY:    string
  AGE:            number
  POSITION:       string
  SUB_POSITIONS:  string
  OVERALL:        number
  STAMINA:        number
  POTENTIAL:      number
  PAC: number; SHO: number; PAS: number; DRI: number; DEF: number; PHY: number
  REF: number; HAN: number; KIK: number; POS_GK: number
  SALARY:         number
  CONTRACT_END:   string
}

interface Props {
  auth:             AuthState
  clubCode:         number
  canViewPotential: boolean
}

export default function Squad({ auth, clubCode, canViewPotential }: Props) {
  const [rows,    setRows]    = useState<PlayerRow[]>([])
  const [loading, setLoading] = useState(true)
  const [err,     setErr]     = useState('')
  const [position, setPosition] = useState<string>('')
  const [minOvr,   setMinOvr]   = useState<number>(0)
  const [minSta,   setMinSta]   = useState<number>(0)
  const [actionMsg, setActionMsg] = useState<{text: string, ok: boolean} | null>(null)
  const [sortKey,  setSortKey]  = useState<SortKey>('POSITION')
  const [sortDir,  setSortDir]  = useState<SortDir>('asc')

  async function handleListForSale(p: PlayerRow) {
    const guess = Math.round(p.OVERALL * p.OVERALL * 1000000)
    const input = window.prompt(`${p.PLAYER_NAME} 매물 등록\n참고가(나이·잠재 미반영): ₩${guess.toLocaleString()}\n\n매물 가격(₩)을 입력하세요:`, String(guess))
    if (!input) return
    const price = Number(input.replace(/[^0-9]/g, ''))
    if (!price || price <= 0) { setActionMsg({ text: '잘못된 가격', ok: false }); return }
    const r = await apiTransferListUser(auth.token, p.PLAYER_CODE, price)
    setActionMsg({ text: r.resultMessage, ok: r.resultCode === '00' })
  }

  function toggleSort(k: SortKey) {
    if (sortKey === k) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortKey(k); setSortDir(k === 'OVERALL' || k === 'POTENTIAL' || k === 'STAMINA' || k === 'SALARY' ? 'desc' : 'asc') }
  }
  const arrow = (k: SortKey) => sortKey === k ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''

  useEffect(() => {
    let mounted = true
    setLoading(true); setErr('')
    apiPlayerList(auth.token, clubCode).then(r => {
      if (!mounted) return
      if (r.resultCode !== '00') { setErr(r.resultMessage || '조회 실패'); setLoading(false); return }
      try {
        const obj = JSON.parse(r.resultData)
        setRows((obj.players ?? []) as PlayerRow[])
      } catch {
        setErr('응답 파싱 실패')
      } finally { setLoading(false) }
    }).catch(e => { setErr(e?.message ?? '네트워크 오류'); setLoading(false) })
    return () => { mounted = false }
  }, [auth.token, clubCode])

  const filtered = useMemo(() => {
    return rows
      .filter(p => position === '' || p.POSITION === position || p.SUB_POSITIONS.split(',').map(s => s.trim()).includes(position))
      .filter(p => minOvr === 0 || p.OVERALL >= minOvr)
      .filter(p => minSta === 0 || p.STAMINA >= minSta)
  }, [rows, position, minOvr, minSta])

  const sorted = useMemo(() => {
    const arr = [...filtered]
    arr.sort((a, b) => {
      const va = a[sortKey] as number | string
      const vb = b[sortKey] as number | string
      if (typeof va === 'number' && typeof vb === 'number') return sortDir === 'asc' ? va - vb : vb - va
      const sa = String(va ?? ''), sb = String(vb ?? '')
      return sortDir === 'asc' ? sa.localeCompare(sb, 'ko') : sb.localeCompare(sa, 'ko')
    })
    return arr
  }, [filtered, sortKey, sortDir])

  if (loading) return <div className="page-pad">선수 목록 불러오는 중…</div>
  if (err)     return <div className="page-pad msg">{err}</div>

  return (
    <div className="page-pad">
      <h2>선수단 ({sorted.length} / {rows.length}명)</h2>

      {actionMsg && <p className={actionMsg.ok ? 'msg ok' : 'msg'}>{actionMsg.text}</p>}

      <section className="card squad-filter">
        <span>포지션:</span>
        <select value={position} onChange={e => setPosition(e.target.value)}>
          {POSITIONS.map(p => <option key={p} value={p}>{p === '' ? '전체' : p}</option>)}
        </select>
        <span>최소 종합:</span>
        <input type="number" min={0} max={99} value={minOvr || ''} onChange={e => setMinOvr(Number(e.target.value))} placeholder="제한 없음" />
        <span>최소 체력:</span>
        <input type="number" min={0} max={100} value={minSta || ''} onChange={e => setMinSta(Number(e.target.value))} placeholder="제한 없음" />
        <button onClick={() => { setPosition(''); setMinOvr(0); setMinSta(0) }}>초기화</button>
      </section>

      <table className="data-table sortable">
        <thead>
          <tr>
            <th onClick={() => toggleSort('POSITION')}>포지션{arrow('POSITION')}</th>
            <th onClick={() => toggleSort('PLAYER_NAME')}>이름{arrow('PLAYER_NAME')}</th>
            <th onClick={() => toggleSort('NATIONALITY')}>국적{arrow('NATIONALITY')}</th>
            <th onClick={() => toggleSort('AGE')}>나이{arrow('AGE')}</th>
            <th onClick={() => toggleSort('OVERALL')}>종합{arrow('OVERALL')}</th>
            <th onClick={() => toggleSort('STAMINA')}>체력{arrow('STAMINA')}</th>
            {canViewPotential && <th onClick={() => toggleSort('POTENTIAL')}>잠재{arrow('POTENTIAL')}</th>}
            <th>주능력</th>
            <th onClick={() => toggleSort('SALARY')}>주급{arrow('SALARY')}</th>
            <th onClick={() => toggleSort('CONTRACT_END')}>계약만료{arrow('CONTRACT_END')}</th>
            <th>액션</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map(p => {
            const isGK = p.POSITION === 'GK'
            const skills = isGK
              ? `반응 ${p.REF} / 핸들링 ${p.HAN} / 킥 ${p.KIK} / 포지셔닝 ${p.POS_GK}`
              : `속도 ${p.PAC} · 슈팅 ${p.SHO} · 패스 ${p.PAS} · 드리블 ${p.DRI} · 수비 ${p.DEF} · 피지컬 ${p.PHY}`
            return (
              <tr key={p.PLAYER_CODE}>
                <td className="pos">{p.POSITION}</td>
                <td>{p.PLAYER_NAME}</td>
                <td>{p.NATIONALITY}</td>
                <td>{p.AGE}</td>
                <td className="ovr">{p.OVERALL}</td>
                <td className={p.STAMINA < 40 ? 'sta-cell low' : p.STAMINA < 60 ? 'sta-cell mid' : 'sta-cell'}>{p.STAMINA}</td>
                {canViewPotential && <td className="pot">{p.POTENTIAL > 0 ? p.POTENTIAL : '-'}</td>}
                <td className="skills">{skills}</td>
                <td className="num">₩ {p.SALARY.toLocaleString()}</td>
                <td>{(p.CONTRACT_END || '').slice(0, 10)}</td>
                <td>
                  <button className="btn-list-sale" onClick={() => handleListForSale(p)}>매물 등록</button>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
